import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { IAgent } from "../listAgentSlice/listAgentSlice";
import { Contact } from "../authSliceLogin/authSliceLogin";
import { IManager } from "../../../interfaces/IManager";
import { IFixPersonToManager } from "../../../interfaces/store/slices/listAgentSlice";

interface IManagerFormInput {
  id?: number;
  surname: string;
  name: string;
  patronymic: string | null;
  email: string;
  password: string;
  is_enabled?: boolean;
  agents: IFixPersonToManager[];
  contacts?: Contact[];
}

interface IManagerForm {
  MANAGER_FORM_DISABLED: boolean;
  MANAGER_FORM_INITIAL: IManagerFormInput;
  MANAGER_FORM_TYPE: "EDIT" | "INFO" | "ADD";
}

interface IListManagerState {
  LIST_MANAGER_ACTIVE: IManager[];
  LIST_MANAGER_ARCHIVE: IManager[];
  SELECTED_MANAGER: IManager | null;
  MANAGER_FORM: IManagerForm;
}

const initialState: IListManagerState = {
  LIST_MANAGER_ACTIVE: [],
  LIST_MANAGER_ARCHIVE: [],
  SELECTED_MANAGER: null,
  MANAGER_FORM: {
    MANAGER_FORM_DISABLED: true,
    MANAGER_FORM_INITIAL: {
      id: 0,
      surname: "",
      name: "",
      patronymic: "",
      email: "",
      password: "",
      agents: [],
    },
    MANAGER_FORM_TYPE: "INFO",
  },
};

const setFormManagerData = (manager: IManager) => {
  return {
    id: manager.id,
    surname: manager.surname,
    name: manager.name,
    patronymic: manager.patronymic,
    email: manager.email,
    password: manager.password,
    phone_number: manager.phone_number,
    agents: manager.agents,
  };
};

// страница списка менеджеров
const listManagerSlice = createSlice({
  name: "LIST_MANAGER_SLICE",
  initialState: initialState,
  reducers: {
    // обновить список менеджеров
    UP_DATE_LIST_MANAGER: (
      state,
      actions: PayloadAction<IManager[], string>,
    ) => {
      const listManager = actions.payload.reduce(
        (acc, obj) => {
          if (obj.is_enabled === true) {
            acc.active.push(obj);
          } else {
            acc.archive.push(obj);
          }
          return acc;
        },
        { active: [], archive: [] } as {
          active: IManager[];
          archive: IManager[];
        },
      );
      state.LIST_MANAGER_ACTIVE = listManager.active;
      state.LIST_MANAGER_ARCHIVE = listManager.archive;
      if (listManager.active.length > 0) {
        state.SELECTED_MANAGER = listManager.active[0];
        state.MANAGER_FORM.MANAGER_FORM_INITIAL = setFormManagerData(
          listManager.active[0],
        );
      }
    },
    // изменить выбранного менеджера
    CHANGE_SELECTED_MANAGER: (
      state,
      actions: PayloadAction<IManager, string>,
    ) => {
      state.SELECTED_MANAGER = actions.payload;
    },
    // изменить начальные значения формы вывода информации о менеджере
    CHANGE_MANAGER_FORM_INITIAL: (
      state,
      actions: PayloadAction<IManager, string>,
    ) => {
      if (actions.payload) {
        state.MANAGER_FORM.MANAGER_FORM_INITIAL = setFormManagerData(
          actions.payload,
        );
      }
    },
    // изменить доступность полей формы для менеджера
    CHANGE_MANAGER_FORM_DISABLE: (
      state,
      actions: PayloadAction<boolean, string>,
    ) => {
      state.MANAGER_FORM.MANAGER_FORM_DISABLED = actions.payload;
    },
    // изменить тип формы информации о менеджере
    CHANGE_MANAGER_FORM_TYPE: (
      state,
      actions: PayloadAction<"EDIT" | "INFO" | "ADD", string>,
    ) => {
      state.MANAGER_FORM.MANAGER_FORM_TYPE = actions.payload;
    },
    // изменить данные формы менеджера
    MANAGER_EDIT: (state, actions: PayloadAction<IManager, string>) => {
      state.SELECTED_MANAGER = actions.payload;
      state.LIST_MANAGER_ACTIVE = state.LIST_MANAGER_ACTIVE.map(
        (manager: IManager) =>
          manager.id === actions.payload.id ? actions.payload : manager,
      );
      state.MANAGER_FORM.MANAGER_FORM_INITIAL = setFormManagerData(
        actions.payload,
      );
    },
    //добавить менеджера
    ADD_NEW_MANAGER: (state, actions: PayloadAction<IManager, string>) => {
      state.LIST_MANAGER_ACTIVE.push(actions.payload);
    },
    // добавить менеджера в архив
    MANAGER_ARCHIVE: (
      state,
      actions: PayloadAction<
        { manager_id: number; is_enabled: boolean },
        string
      >,
    ) => {
      const updatedListManager = state.LIST_MANAGER_ACTIVE.concat(
        state.LIST_MANAGER_ARCHIVE,
      ).map((manager: IManager) => {
        if (manager.id === actions.payload.manager_id) {
          return {
            ...manager,
            is_enabled: actions.payload.is_enabled,
          };
        }
        return manager;
      });

      const listManager = updatedListManager.reduce(
        (
          acc: { active: IManager[]; archive: IManager[] },
          manager: IManager,
        ) => {
          if (manager.is_enabled === true) {
            acc.active.push(manager);
          } else {
            acc.archive.push(manager);
          }
          return acc;
        },
        { active: [], archive: [] } as {
          active: IManager[];
          archive: IManager[];
        },
      );
      state.LIST_MANAGER_ACTIVE = listManager.active;
      state.LIST_MANAGER_ARCHIVE = listManager.archive;
    },
    //изменить прикрепленных агентов
    SET_MANAGER_AGENT: (
      state,
      actions: PayloadAction<
        { manager_id: number; agetns: IFixPersonToManager[] },
        string
      >,
    ) => {
      state.LIST_MANAGER_ACTIVE = state.LIST_MANAGER_ACTIVE.map(
        (manager: IManager) => {
          if (manager.id === actions.payload.manager_id) {
            return {
              ...manager,
              agents: actions.payload.agetns,
            };
          }
          return manager;
        },
      );
      if (state.SELECTED_MANAGER) {
        state.SELECTED_MANAGER = {
          ...state.SELECTED_MANAGER,
          agents: actions.payload.agetns || [],
        };
      }
    },
  },
});

export const {
  UP_DATE_LIST_MANAGER,
  CHANGE_MANAGER_FORM_INITIAL,
  CHANGE_SELECTED_MANAGER,
  CHANGE_MANAGER_FORM_DISABLE,
  CHANGE_MANAGER_FORM_TYPE,
  MANAGER_EDIT,
  ADD_NEW_MANAGER,
  MANAGER_ARCHIVE,
  SET_MANAGER_AGENT,
} = listManagerSlice.actions;
export default listManagerSlice.reducer;
